import { clsx } from "clsx";
import React, { forwardRef } from "react";
import { twMerge } from "tailwind-merge";

import { ConditionalTooltip } from "../Tooltip/Tooltip";
import { LoadingSpinner } from "../LoadingSpinner/LoadingSpinner";

type ButtonIntent = keyof typeof intentVariantStyles;
type ButtonVariant = keyof (typeof intentVariantStyles)[ButtonIntent];

export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  className?: string;
  intent?: ButtonIntent;
  variant?: ButtonVariant;
  leadingIcon?: React.ReactNode;
  trailingIcon?: React.ReactNode;
  tooltipContent?: React.ReactNode;
  loading?: boolean;
};

const intentVariantStyles = {
  default: {
    primary:
      "bg-blue-500 text-white not-disabled:hover:bg-blue-600 active:bg-blue-700 shadow",
    secondary:
      "bg-transparent text-blue-500 not-disabled:hover:text-blue-600 not-disabled:hover:bg-blue-100 active:text-blue-700 border border-blue-500 not-disable:hover:border-blue-600 active:border-blue-700 active:bg-blue-100 shadow",
    tertiary:
      "bg-transparent not-disabled:hover:bg-blue-100 active:bg-blue-200 text-blue-500 not-disabled:hover:text-blue-600 active:text-blue-700",
    link: "bg-transparent text-blue-500 not-disabled:hover:underline px-0",
  },
  danger: {
    primary:
      "bg-red-600 text-white not-disabled:hover:bg-red-700 active:bg-red-800 shadow",
    secondary:
      "bg-transparent text-red-600 not-disabled:hover:text-red-700 not-disabled:hover:bg-red-50 active:text-red-800 border border-red-600 not-disabled:hover:border-red-700 active:border-red-800 active:bg-red-100 shadow",
    tertiary:
      "bg-transparent not-disabled:hover:bg-red-50 active:bg-red-100 text-red-600 not-disabled:hover:text-red-700 active:text-red-800",
    link: "bg-transparent text-red-600 not-disabled:hover:underline px-0",
  },
} as const;

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      leadingIcon,
      trailingIcon,
      variant = "primary",
      intent = "default",
      children,
      type = "button",
      tooltipContent,
      loading,
      ...rest
    },
    ref,
  ) => {
    const buttonItself = (
      <button
        ref={ref}
        {...rest}
        disabled={rest.disabled || loading}
        type={type}
        className={twMerge(
          clsx(
            "border-box inline-flex h-12 items-center justify-center gap-2 rounded-md px-6 py-4 text-center text-sm leading-none font-medium whitespace-nowrap outline-blue-50 transition-all not-disabled:cursor-pointer focus:outline-[3px] disabled:opacity-30",
            intentVariantStyles[intent][variant],
          ),
          className,
          rest.disabled && "cursor-not-allowed",
        )}
      >
        {loading ? (
          <LoadingSpinner className="size-4" />
        ) : (
          <>
            {leadingIcon}
            {children}
            {trailingIcon}
          </>
        )}
      </button>
    );

    return (
      <ConditionalTooltip condition={!!tooltipContent} content={tooltipContent}>
        {buttonItself}
      </ConditionalTooltip>
    );
  },
);
